/**
 * Catch Up — fully client-side. The player has already fetched + parsed both
 * the M3U and the EPG (via PlaylistDataContext). We just slice the data
 * locally and build the per-programme catch-up URL on demand.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { usePlaylistData } from "../lib/PlaylistDataContext";
import { catchupChannels } from "../lib/m3uParser";
import { pastProgrammes, buildCatchupUrl } from "../lib/epgParser";
import { fetchXtreamCatchupTable, buildXtreamCatchupUrl } from "../lib/xtreamApi";
import { isElectron } from "../lib/deviceId";
import { ArrowLeft, History, Loader2, Play, Tv, Calendar } from "lucide-react";
import PosterImage from "../components/PosterImage";
import { PLAYER } from "../lib/testIds";

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString([], {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtDuration(startTs, stopTs) {
  const mins = Math.max(0, Math.round((stopTs - startTs) / 60));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function dayKey(ts) {
  const d = new Date(ts * 1000);
  return d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
}

// Xtream returns title/description base64-encoded — quietly fall back to plain text
// if it's not a valid base64 string.
function tryDecode(s) {
  if (!s || typeof s !== "string") return s;
  try {
    return decodeURIComponent(escape(atob(s)));
  } catch {
    return s;
  }
}

export default function CatchUp() {
  const navigate = useNavigate();
  const { playlists } = useOutletContext();
  const { channels, epgByChannel, status, xtream } = usePlaylistData();

  // Channels with catch-up come from two sources — merge them (deduped by
  // stream_url, which is STABLE across IdbPutPlaylist rewrites; `id`
  // gets regenerated every import and produced the "two identical tiles"
  // bug reported in v1.4.41).
  const allCatchupChannels = useMemo(() => {
    const byUrl = new Map();

    // 1. M3U `catchup-source` / `catchup-days` / `catchup` tagged channels.
    for (const c of catchupChannels(channels)) {
      if (c.stream_url && !byUrl.has(c.stream_url)) byUrl.set(c.stream_url, c);
    }

    // 2. Xtream live channels with tv_archive=1 (matches vupro's behaviour).
    if (xtream?.creds) {
      for (const c of channels) {
        if (
          c.content_type === "live_tv" &&
          c.xc_id &&
          c.tv_archive === true &&
          c.stream_url &&
          !byUrl.has(c.stream_url)
        ) {
          byUrl.set(c.stream_url, c);
        }
      }
    }
    return Array.from(byUrl.values());
  }, [channels, xtream]);

  // Keep these around just so the empty-state copy still picks the right
  // wording — they no longer drive the channel list itself.
  const tagged = useMemo(() => catchupChannels(channels), [channels]);
  const source =
    tagged.length > 0
      ? "m3u_tag"
      : xtream?.creds
      ? "xtream"
      : "none";

  const [activeChannel, setActiveChannel] = useState(null);
  const [filter, setFilter] = useState("");
  const [xcPrograms, setXcPrograms] = useState(null); // Xtream catchup-table override

  useEffect(() => {
    if (!activeChannel && allCatchupChannels.length > 0) {
      setActiveChannel(allCatchupChannels[0]);
    }
    if (activeChannel && !allCatchupChannels.find((c) => c.stream_url === activeChannel.stream_url)) {
      setActiveChannel(allCatchupChannels[0] || null);
    }
  }, [allCatchupChannels, activeChannel]);

  // Xtream catch-up — when the playlist is an Xtream panel we prefer the
  // panel's own catchup table (same data vupro.apk uses) over the generic
  // XMLTV EPG. Falls back to EPG history when the panel doesn't expose it.
  useEffect(() => {
    setXcPrograms(null);
    if (!activeChannel || !xtream?.creds || !activeChannel.xc_id) return;
    let cancelled = false;
    const fetchFn = async (url) => {
      if (isElectron() && window.electronAPI?.fetchUrl) {
        const r = await window.electronAPI.fetchUrl(url);
        if (!r?.ok) throw new Error(r?.error || `HTTP ${r?.status}`);
        return r.body;
      }
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return await r.text();
    };
    fetchXtreamCatchupTable(xtream.creds, activeChannel.xc_id, fetchFn)
      .then((rows) => {
        if (cancelled) return;
        const nowTs = Math.floor(Date.now() / 1000);
        // Xtream returns start/end as either unix-secs or YYYY-MM-DD HH:MM:SS strings.
        const parsed = rows
          .map((r) => {
            const start = parseInt(r.start_timestamp || r.start, 10) ||
              (r.start ? Math.floor(new Date(r.start.replace(" ", "T") + "Z").getTime() / 1000) : 0);
            const stop = parseInt(r.stop_timestamp || r.end, 10) ||
              (r.end ? Math.floor(new Date(r.end.replace(" ", "T") + "Z").getTime() / 1000) : 0);
            // has_archive is 1 when archive is available. Many panels omit
            // the field entirely on archived channels — treat "missing" as
            // available (channel-level tv_archive already gated us in).
            const archiveFlag =
              r.has_archive === undefined || r.has_archive === null
                ? true
                : r.has_archive === 1 || r.has_archive === "1" || r.has_archive === true;
            return {
              title: tryDecode(r.title) || "Untitled",
              description: tryDecode(r.description) || null,
              start: new Date(start * 1000).toISOString(),
              stop: new Date(stop * 1000).toISOString(),
              start_ts: start,
              stop_ts: stop,
              has_archive: archiveFlag,
            };
          })
          .filter((p) => p.stop_ts > 0 && p.stop_ts <= nowTs && p.has_archive)
          .sort((a, b) => b.start_ts - a.start_ts)
          .slice(0, 200);
        setXcPrograms(parsed);
      })
      .catch(() => !cancelled && setXcPrograms([]));
    return () => {
      cancelled = true;
    };
  }, [activeChannel, xtream]);

  const filteredChannels = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return allCatchupChannels;
    return allCatchupChannels.filter(
      (c) => c.name?.toLowerCase().includes(term) || c.group?.toLowerCase().includes(term)
    );
  }, [allCatchupChannels, filter]);

  // Past programmes for the active channel:
  //   1. Prefer Xtream's get_simple_data_table when the panel is Xtream
  //   2. Fall back to the generic XMLTV EPG
  const programs = useMemo(() => {
    if (xcPrograms && xcPrograms.length > 0) return xcPrograms;
    if (!activeChannel?.tvg_id) return [];
    const days = activeChannel.catchup_days || 7;
    return pastProgrammes(epgByChannel, activeChannel.tvg_id, { days, limit: 120 });
  }, [activeChannel, epgByChannel, xcPrograms]);

  // Group programmes by calendar day
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const p of programs) {
      const k = dayKey(p.start_ts);
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(p);
    }
    return Array.from(buckets.entries());
  }, [programs]);

  const epgAvailable =
    (xcPrograms && xcPrograms.length > 0) ||
    (!!activeChannel?.tvg_id && (epgByChannel[activeChannel.tvg_id] || []).length > 0);

  const playProgramme = (p) => {
    const duration = Math.max(1, p.stop_ts - p.start_ts);
    // Xtream catchup URL takes priority when we have stream_id + creds.
    // We get both candidate URL formats — Watch.jsx auto-tries the fallback
    // if the primary 404s (some panels only support one of the two formats).
    let url;
    let fallbackUrl = null;
    if (xtream?.creds && activeChannel.xc_id) {
      const built = buildXtreamCatchupUrl(
        xtream.creds,
        activeChannel.xc_id,
        p.start_ts,
        duration
      );
      url = built?.primary || null;
      fallbackUrl = built?.fallback || null;
    } else {
      url = buildCatchupUrl({
        baseStreamUrl: activeChannel.stream_url,
        catchupSource: activeChannel.catchup_source,
        catchupType: activeChannel.catchup,
        startTs: p.start_ts,
        durationSeconds: duration,
      });
    }
    navigate("/player/watch", {
      state: {
        stream_url: url,
        fallback_url: fallbackUrl,
        name: `${activeChannel.name} — ${p.title || "Catch-up"}`,
        logo: activeChannel.logo_url || "",
        type: "catchup",
      },
    });
  };

  if (playlists.length === 0) {
    return (
      <div className="py-24 text-center text-slate-400">
        <Tv className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p>No playlists assigned to this device.</p>
      </div>
    );
  }

  return (
    <div data-testid="player-catchup-screen" className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
      {/* Channel list */}
      <div>
        <button
          data-testid={PLAYER.btnBack}
          onClick={() => navigate("/player/menu")}
          className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Menu
        </button>
        <h2 className="text-xl font-bold mb-1 flex items-center gap-2">
          <History className="w-5 h-5 text-emerald-400" />
          Catch Up TV
        </h2>
        <p className="text-xs text-slate-500 mb-3">
          {source === "m3u_tag"
            ? "Channels with catch-up enabled by your provider."
            : source === "epg"
            ? "Channels with EPG history available."
            : status === "loading"
            ? "Loading playlist…"
            : "No catch-up data available."}
        </p>
        <input
          data-testid="catchup-channel-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter channels…"
          className="w-full bg-[#11141b] border border-white/10 focus:border-orange-500/50 text-sm rounded-md px-3 py-2 mb-3 outline-none placeholder:text-slate-600"
        />
        <div className="space-y-1 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
          {status === "loading" ? (
            <div className="text-sm text-slate-500 p-2 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : filteredChannels.length === 0 ? (
            <div className="text-sm text-slate-500 p-3 text-center">
              No catch-up channels in this playlist.
            </div>
          ) : (
            filteredChannels.map((c) => (
              <button
                key={c.stream_url || c.id}
                data-testid={`catchup-channel-${c.id}`}
                onClick={() => setActiveChannel(c)}
                className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-md transition-colors outline-none focus:ring-4 focus:ring-emerald-500 focus:z-10 relative ${
                  activeChannel?.stream_url === c.stream_url
                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                    : "text-slate-300 hover:bg-white/5 border border-transparent"
                }`}
              >
                <div className="w-10 h-10 shrink-0 bg-black rounded overflow-hidden flex items-center justify-center">
                  {c.logo_url ? (
                    <PosterImage
                      src={c.logo_url}
                      alt=""
                      fallbackIcon={Tv}
                      fallbackIconClassName="w-4 h-4 text-slate-700"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Tv className="w-4 h-4 text-slate-700" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {c.group || "—"}
                    {c.catchup_days ? ` · ${c.catchup_days}d` : ""}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Programme timeline */}
      <div>
        {!activeChannel ? (
          <div className="py-24 text-center text-slate-500 text-sm">
            Select a channel to see its catch-up timeline.
          </div>
        ) : !epgAvailable ? (
          <div className="py-24 text-center text-slate-500 text-sm">
            No EPG entries for this channel — make sure your playlist&apos;s
            <code className="mx-1 text-slate-300">url-tvg</code>is reachable.
          </div>
        ) : programs.length === 0 ? (
          <div className="py-24 text-center text-slate-500 text-sm">
            No past programmes found for{" "}
            <span className="text-slate-300">{activeChannel.name}</span>.
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-3 border-b border-white/5">
              {activeChannel.logo_url && (
                <div className="w-10 h-10 bg-black rounded overflow-hidden">
                  <PosterImage
                    src={activeChannel.logo_url}
                    alt=""
                    fallbackIcon={Tv}
                    fallbackIconClassName="w-4 h-4 text-slate-700"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              <div>
                <div className="text-lg font-semibold">{activeChannel.name}</div>
                <div className="text-xs text-slate-500">
                  {programs.length} past programme{programs.length === 1 ? "" : "s"} available
                </div>
              </div>
            </div>

            {grouped.map(([day, items]) => (
              <section key={day}>
                <h3 className="text-xs uppercase tracking-wider text-emerald-300 font-semibold mb-2 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  {day}
                </h3>
                <div className="space-y-1.5">
                  {items.map((p) => (
                    <button
                      key={`${p.start_ts}-${p.title}`}
                      data-testid={`catchup-prog-${p.start_ts}`}
                      onClick={() => playProgramme(p)}
                      className="w-full group text-left flex items-start gap-3 px-3 py-2.5 rounded-md bg-[#11141b] hover:bg-emerald-500/10 border border-white/5 hover:border-emerald-500/40 transition-colors outline-none focus:ring-4 focus:ring-emerald-500 focus:border-emerald-500 focus:z-10 relative"
                    >
                      <div className="text-[11px] tabular-nums text-slate-400 shrink-0 w-28 pt-0.5">
                        {fmtTime(p.start)}
                        <div className="text-[10px] text-slate-600">
                          {fmtDuration(p.start_ts, p.stop_ts)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate group-hover:text-emerald-200">
                          {p.title || "Untitled"}
                        </div>
                        {p.description && (
                          <div className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">
                            {p.description}
                          </div>
                        )}
                      </div>
                      <div className="w-8 h-8 rounded-full bg-white/5 group-hover:bg-emerald-400 text-slate-400 group-hover:text-black flex items-center justify-center shrink-0 transition-colors">
                        <Play className="w-3.5 h-3.5 fill-current" />
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
